import { Button } from "@msuite/ui/button";
import { useLoading } from "@msuite/ui/hooks";
import { SuccessIcon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import type { Nullable } from "@msuite/utils/types";
import React from "react";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { PersonioSyncRole } from "./personio-sync-role";
import type { ThirdPartyIntegrationStateProps } from "./types";

/** Props Interface */
interface ThirdPartyIntegrationActiveProps extends ThirdPartyIntegrationStateProps {
	createdAt: Nullable<string>;
}

export function ThirdPartyIntegrationActive({
	createdAt,
	integrationType,
	refetch,
}: ThirdPartyIntegrationActiveProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { canManageRoles, citadel } = useCitadel();
	const asyncModal = useAsyncModal();
	const deactivateLoader = useLoading();

	/** Computed */
	// Reading whether an integration exists and removing it are two rules. A reader who holds only
	// the first opens this tab legitimately, so the button that would 403 is not rendered for them.
	const canDeleteIntegration = citadel?.can("delete", "Integration") === true;

	/** Functions */
	async function handleOnDeactivateIntegration() {
		try {
			deactivateLoader.start();
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
			const response = await client.integrations[":integrationType"].$delete({
				param: { integrationType },
			});
			// The delete throws when the organisation has no integration of this type, and the
			// route answers that as a failure body. Without `responseHandler` the card refetches,
			// finds the integration still there, and the person sees the button do nothing.
			await responseHandler(response);
			await refetch();
		} catch {
			// `responseHandler` has already put the server's message in front of the person.
		} finally {
			deactivateLoader.stop();
		}
	}

	/** Render */
	return (
		<React.Fragment>
			<div className="v-stack gap-0">
				<div className="h-stack justify-between">
					<div className="h-stack gap-4 items-center">
						<SuccessIcon />
						<div className="v-stack gap-0">
							<p>{t("integrations.integration_is_active")}</p>
							<p className="label">
								{t("integrations.integrated_at", {
									date: moment(createdAt).longFormat(),
								})}
							</p>
						</div>
					</div>
					{canDeleteIntegration && (
						<Button
							isLoading={deactivateLoader.isLoading}
							onClick={handleOnDeactivateIntegration}
							variant="danger-ghost"
						>
							{t("integrations.deactivate_integration")}
						</Button>
					)}
				</div>
				{/* The sync hands out a role with every person it creates, so the setting belongs
				    where the integration is configured — and only once it is active. Gated at the
				    mount, not inside: the control's reads require owner-or-admin, and this card is
				    on a tab anyone can open, so mounting it for a plain reader would fire two
				    refused requests and their toasts before any inner guard could return null. */}
				{integrationType === "personio" && canManageRoles && <PersonioSyncRole />}
			</div>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("integrations.deactivate_integration_confirmation")}
					confirmLabel={t("integrations.deactivate_integration")}
				/>
			)}
		</React.Fragment>
	);
}
